import React from "react";
import "../styles/MarketStateBadges.css";

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getTone = (value, isPositiveGood = true) => {
  const n = toNumber(value);
  if (n === null) return "neutral";
  if (isPositiveGood) return n > 0 ? "positive" : n < 0 ? "negative" : "neutral";
  return n > 0 ? "negative" : n < 0 ? "positive" : "neutral";
};

const getDirLabel = (dir) => {
  const d = String(dir || "").toUpperCase();
  if (d.includes("ALCISTA")) return { label: "ALCISTA", tone: "positive" };
  if (d.includes("BAJISTA")) return { label: "BAJISTA", tone: "negative" };
  return { label: "NEUTRAL", tone: "neutral" };
};

const getFlowTone = (dir) => {
  const d = String(dir || "").toUpperCase();
  if (d.includes("ALCISTA")) return "positive";
  if (d.includes("BAJISTA")) return "negative";
  return "neutral";
};

/**
 * Componente que muestra los 4 indicadores de profundidad de mercado (Order Flow):
 *   1. Barrido de liquidez
 *   2. Flujo de órdenes
 *   3. VWAP anclado
 *   4. Perfil de volumen
 *
 * Los datos vienen en order.sl_tp_info.orderflow_* (guardados por el backend)
 * o directamente en order.orderflow_*.
 */
const OrderFlowStatus = ({ order }) => {
  if (!order) return null;

  // Los datos pueden venir anidados en sl_tp_info o directamente en la orden
  const slTpInfo = order?.sl_tp_info || {};
  const of = slTpInfo || order || {};

  const barrido = of.barrido_liquidez || of.orderflow_barrido_liquidez || {};
  const flujo = of.flujo_ordenes || of.orderflow_flujo_ordenes || {};
  const vwap = of.vwap_anclado || of.orderflow_vwap_anclado || {};
  const perfil = of.perfil_volumen || of.orderflow_perfil_volumen || {};
  const senal = of.senal_entrada || of.orderflow_senal_entrada || "NEUTRAL";
  const confianza = toNumber(of.confianza || of.orderflow_confianza);
  const bloquear = of.bloquear_compra || of.orderflow_bloquear_compra || false;
  const motivoBloqueo = of.motivo_bloqueo || of.orderflow_motivo_bloqueo || "";

  // Si no hay datos de order flow en absoluto, no mostrar nada
  const tieneDatos =
    barrido.detectado ||
    (flujo.direccion && flujo.direccion !== "NEUTRAL") ||
    (vwap.vwap_anclado !== null && vwap.vwap_anclado !== undefined) ||
    (perfil.poc !== null && perfil.poc !== undefined);

  if (!tieneDatos) return null;

  const dirLabel = getDirLabel(senal);
  const flujoLabel = getDirLabel(flujo.direccion);
  const barridoDir = String(barrido.direccion || "").toUpperCase();

  return (
    <div className="orderflow-status">
      <div className="orderflow-header">
        <span className="orderflow-title">📊 Profundidad de Mercado</span>
        {bloquear && (
          <span className="orderflow-block-badge tone-danger">
            🚫 BLOQUEA COMPRA
          </span>
        )}
      </div>

      <div className="orderflow-grid">
        {/* Barrido de liquidez */}
        <div className="orderflow-metric">
          <div className="orderflow-metric-label">Barrido Liquidez</div>
          <div
            className={`orderflow-metric-value ${
              barridoDir === "BAJISTA" ? "tone-negative" : barridoDir === "ALCISTA" ? "tone-positive" : "tone-neutral"
            }`}
          >
            {barrido.detectado ? (barridoDir === "BAJISTA" ? "🔴 Bajista" : "🟢 Alcista") : "—"}
          </div>
          <div className="orderflow-metric-hint">
            {barrido.detectado ? `Confianza ${(barrido.confianza * 100).toFixed(0)}%` : "Sin datos"}
            {barrido.nivel ? ` | Nivel $${Number(barrido.nivel).toFixed(2)}` : ""}
          </div>
        </div>

        {/* Flujo de órdenes */}
        <div className="orderflow-metric">
          <div className="orderflow-metric-label">Flujo Órdenes</div>
          <div
            className={`orderflow-metric-value tone-${getFlowTone(flujo.direccion)}`}
          >
            {flujo.direccion && flujo.direccion !== "NEUTRAL"
              ? `${flujoLabel.label} ${(flujo.fuerza * 100).toFixed(0)}%`
              : "—"}
          </div>
          <div className="orderflow-metric-hint">
            {flujo.diferencial_pct !== null && flujo.diferencial_pct !== undefined
              ? `Diferencial ${flujo.diferencial_pct > 0 ? "+" : ""}${flujo.diferencial_pct.toFixed(0)}%`
              : `Trades ${flujo.num_trades || 0}`}
          </div>
        </div>

        {/* VWAP Anclado */}
        <div className="orderflow-metric">
          <div className="orderflow-metric-label">VWAP Anclado</div>
          <div
            className={`orderflow-metric-value ${
              vwap.esta_arriba ? "tone-warning" : "tone-neutral"
            }`}
          >
            {vwap.vwap_anclado !== null && vwap.vwap_anclado !== undefined
              ? `$${Number(vwap.vwap_anclado).toFixed(2)}`
              : "—"}
          </div>
          <div className="orderflow-metric-hint">
            {vwap.precio_vs_vwap_pct !== null && vwap.precio_vs_vwap_pct !== undefined
              ? `Precio ${vwap.precio_vs_vwap_pct > 0 ? "+" : ""}${vwap.precio_vs_vwap_pct.toFixed(1)}%`
              : "Sin datos"}
          </div>
        </div>

        {/* Perfil de Volumen */}
        <div className="orderflow-metric">
          <div className="orderflow-metric-label">Perfil Volumen</div>
          <div className="orderflow-metric-value tone-neutral">
            {perfil.poc !== null && perfil.poc !== undefined
              ? `$${Number(perfil.poc).toFixed(2)}`
              : "—"}
          </div>
          <div className="orderflow-metric-hint">
            {perfil.esta_en_valor_area !== null && perfil.esta_en_valor_area !== undefined
              ? perfil.esta_en_valor_area
                ? "En valor área"
                : "Fuera valor área"
              : "Sin datos"}
          </div>
        </div>
      </div>

      {motivoBloqueo && (
        <div className="orderflow-motivo tone-danger">
          ⚠️ {motivoBloqueo}
        </div>
      )}
    </div>
  );
};

export default OrderFlowStatus;
